import React from "react";

import { ELLIPSIS_SX } from "@app/common/styles";

import { Row } from "@rui/Stack";
import { Span, Italic } from "@rui/styles";

import {
  ContentColumn,
  ContentColumnHeader,
  DeletedTurn,
  Speaker,
  SpeakerContainer,
  SpeakerName,
  Timestamp,
  Turn,
} from "./styles";
import EditedChip from "../EditedChip";

export interface TranscriptTurnProps {
  id: string;
  turnId: string;
  children?: React.ReactNode;
  isDeleted?: boolean;
  /** Name of user who most recently edited this turn */
  editedBy?: string;
  /** If a speaker is `undefined`, we will hide the avatar and speaker name/timestamp.
   * This should be used to create the appearance of multiple speech bubbles under
   * a single turn. If a speaker is `null`, that will be treated as a standalone
   * transcript turn with an unknown speaker */
  speaker?: string | null;
  /** Relative timestamp of turn start time. May not be defined for deleted turns */
  timestamp?: string;

  isSpeakerEditable?: boolean;
  onSpeakerClick?: (
    event: React.MouseEvent<HTMLElement>,
    speakerName: string | null,
    turnId: string
  ) => void;
}

const TranscriptTurn = ({
  id,
  turnId,
  children,
  isDeleted = false,
  editedBy,
  speaker,
  timestamp,
  isSpeakerEditable,
  onSpeakerClick,
}: TranscriptTurnProps) => {
  // a speaker will be undefined when we want to show a new speech bubble as
  // part of the same turn as the previous turn
  const isNested = speaker === undefined;

  const handleSpeakerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (typeof speaker !== "undefined" && isSpeakerEditable) {
      onSpeakerClick?.(event, speaker, turnId);
    }
  };

  if (isDeleted) {
    return (
      <DeletedTurn nested={isNested}>
        <Italic>Turn deleted</Italic>
        {editedBy && (
          <Span className="transcript-turn__edited-by" sx={ELLIPSIS_SX}>
            Edited by {editedBy}
          </Span>
        )}
      </DeletedTurn>
    );
  }

  return (
    <Turn id={id} spacing={1.5} nested={isNested}>
      <Speaker
        speaker={speaker}
        isEditable={isSpeakerEditable}
        onClick={handleSpeakerClick}
      />
      <ContentColumn spacing={0.5}>
        {!isNested && (
          <ContentColumnHeader variant="apart">
            <SpeakerContainer>
              {speaker && <SpeakerName>{speaker}</SpeakerName>}
              {timestamp && <Timestamp>{timestamp}</Timestamp>}
            </SpeakerContainer>
            {editedBy && (
              <Row flexShrink={0}>
                <EditedChip editedBy={editedBy} />
              </Row>
            )}
          </ContentColumnHeader>
        )}
        {children}
      </ContentColumn>
    </Turn>
  );
};

export default TranscriptTurn;
